import { formatNumber } from '@trmediaab/zebra-utils';

export const EMPTY_VALUE_CHARACTER = '-';

function formatValueBase(value, formatter) {
  return value == null || value === EMPTY_VALUE_CHARACTER
    ? EMPTY_VALUE_CHARACTER
    : formatter(value);
}

export function formatNumberValue(value, numberFormatOptions, isCent = false) {
  return formatNumber(isCent ? value / 100 : value, numberFormatOptions);
}

/**
 * Formats an odds number to a string. If the value is nullish a fallback character will be returned.
 * @param oddsValueMultipliedBy100 odds number to format. The value is expected to be the actual value multiplied by 100.
 * @returns Formatted string.
 */
export function formatOdds(
  oddsValueMultipliedBy100,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
) {
  return formatValueBase(oddsValueMultipliedBy100, value =>
    formatNumberValue(
      value,
      { minimumFractionDigits, maximumFractionDigits },
      true,
    ),
  );
}

export function formatSpeed(value) {
  return formatValueBase(value, value =>
    formatNumberValue(value, {
      minimumIntegerDigits: 2,
      minimumFractionDigits: 1,
    }),
  );
}

/**
 * Formats a percentage number to a string. If the value is nullish a fallback character will be returned.
 * @param value number to format.
 * @param options An optional configuration object for formatting the value:
 * @param options.rejectZero Whether 0 values should be rejected and return the fallback character. Default false.
 * @param options.isCent Whether the value is a cent value that should be divided by 100. Cannot be combined with isDecimal. Default false.
 * @param options.isDecimal Whether the value is a decimal value that should be multiplied by 100. Cannot be combined with isCent. Default false.
 * @returns Formatted string.
 */
export function formatPercentage(value, options) {
  const { isCent, isDecimal, rejectZero } = {
    isCent: false,
    isDecimal: false,
    rejectZero: false,
    ...options,
  };

  return formatValueBase(value, value_ => {
    const value = isCent ? value_ / 100 : isDecimal ? value_ * 100 : value_;
    const formatOptions =
      value > 0 && value < 10
        ? // One decimal for values over zero and below 10%
          { minimumFractionDigits: 1, maximumFractionDigits: 1 }
        : // Otherwise no decimals
          { maximumFractionDigits: 0 };
    return rejectZero && value === 0
      ? EMPTY_VALUE_CHARACTER
      : `${formatNumberValue(value, formatOptions)}%`;
  });
}
